import { TitleCasePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatFormFieldAppearance, SubscriptSizing } from '@angular/material/form-field';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { CommonUtilityLib, PROMPTS } from '@thehubz/client/core';
import { UserInfo, UsersApiService } from '@thehubz/client/data-access-api';
import { AnyType } from '@thehubz/shared/model';
import { lastValueFrom } from 'rxjs';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';
import { SelectSearchConfig } from './model/select-search-config.model';

const EMPTY_OPTION = PROMPTS.PLACEHOLDER.EMPTY_OPTION;

@Component({
  selector: 'sp-mat-select-search',
  templateUrl: './mat-select-search.component.html',
  styleUrls: ['./mat-select-search.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MatSelectSearchComponent),
      multi: true,
    },
  ],
})
export class MatSelectSearchComponent implements OnChanges, ControlValueAccessor {
  /** data source */
  @Input() dataSource?: AnyType[] = [];
  /** mat form field appearance */
  @Input() appearance: MatFormFieldAppearance = 'outline';
  /** Behaviors for how the subscript height is set. */
  @Input() subscriptSizing: SubscriptSizing = 'fixed';
  /** mat select placeholder */
  @Input() placeholder?: string;
  /** mat select placeholder */
  @Input() inputSearchPlaceHolder?: string;
  /** mat select name attribute */
  @Input() attrName?: string;
  /** search placeholder */
  @Input() searchPlaceholder = PROMPTS.PLACEHOLDER.SEARCH;
  /** additional configuration */
  @Input() config?: SelectSearchConfig;
  /** disable form field */
  @Input() disabledField = false;
  /** data attribute id for test */
  @Input() attrDataTestId?: string;
  /** form control name */
  @Input() formControlName!: string;
  /** enable to show user avatar */
  @Input() showAvatar?: boolean = false;

  /** updates the selected values array */
  @Output() selectedChange = new EventEmitter<AnyType>();
  /** for multiselect option to emit the current checked or unchecked value */
  @Output() optionChanged = new EventEmitter<AnyType>();
  /** emit a removed chip */
  @Output() removeChip = new EventEmitter<AnyType>();

  @ViewChild('search') matSearch!: ElementRef;
  @ViewChild(MatSelect, { static: true }) matSelect!: MatSelect;

  /** the options selected (type string[] | string) */
  selected: AnyType;
  /** hold the search string */
  searchValue!: string;
  /** show first part of the label */
  firstPartOfLabel!: AnyType;
  dataSourceStringType!: boolean;
  dataSourceObjType!: boolean;
  optionChangedObj!: AnyType;

  /** properties used for mat-checkbox */
  allSelected = false;
  showAllSelected = false;
  someSelected = false;
  /** Flag to set class on mat-option to hide selected checkbox for single select items */
  singleSelect = false;
  selectAll = PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_ALL;
  selectOne = PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_ONE;
  selectedInlineImage!: string;

  private onChange!: (value: AnyType) => void;
  private onTouched!: () => void;

  constructor(
    private titlecasePipe: TitleCasePipe,
    private commonUtilityLib: CommonUtilityLib,
    private confirmDialog: ConfirmDialogService,
    private usersApiService: UsersApiService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource'] && changes['dataSource'].currentValue?.length) {
      this.dataSource = changes['dataSource'].currentValue;
      if (Array.isArray(this.dataSource)) {
        this.dataSourceStringType = this.dataSource.length ? this.dataSource.every((data) => typeof data === 'string') : false;
        this.dataSourceObjType = this.dataSource.length ? this.dataSource.every((data) => typeof data === 'object') : false;
        // for selectAll checkbox: set to true if selected length is same as dataSource
        this.selectAllSelected();
        // pre-selection won't work for sortDataSource false condition
        if (
          this.dataSource.length === 1 &&
          !this.config?.multiple &&
          !this.config?.disableSingleSelect &&
          this.config?.sortDataSource === undefined &&
          !this.config?.enableMatOptGroup
        ) {
          this.selected = this.dataSource[0];
          this.singleSelect = true;
          setTimeout(() => {
            this.onSelect({ source: { singleValue: true } as AnyType, value: this.dataSource?.[0] });
          });
        } else {
          this.singleSelect = false;
        }

        if (!this.config?.sortDataSource && this.config?.sortDataSource !== undefined) {
          this.dataSource = JSON.parse(JSON.stringify(this.dataSource));
        } else {
          this.dataSource = this.sortDataSource(this.dataSource);
        }
        // In some cases the dataSource is rendered later
        // Dropdown label will be populated when dataSource is ready
        this.firstPartOfLabel = this.getFistPartOfLabel();
      }
    }
  }

  /**
   * Sort data and also look for topItem property that will always be unsorted above the list
   * @param dataSource
   * @returns
   */
  sortDataSource(dataSource: AnyType[]) {
    return dataSource
      .filter((e) => !!e.topItem) // topItem will be shown unsorted above the list
      .concat(
        dataSource
          .filter((e) => !e.topItem) // separate topItem, which will be shown unsorted above the list
          .sort((a, b) => {
            if (a.name) {
              return a.name.toString().localeCompare(b.name.toString());
            } else {
              return a.toString().localeCompare(b.toString());
            }
          }),
      );
  }

  // ========================= ControlValueAccessor interface methods =========================
  writeValue(value: AnyType): void {
    this.setValue(value, false);
  }
  registerOnChange(fn: AnyType): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: AnyType): void {
    this.onTouched = fn;
  }
  // ==========================================================================================

  /**
   * Mat-select selectionChange event
   * @param event
   */
  onSelect(event: MatSelectChange): void {
    if (this.config?.tagField) {
      this.optionChanged.emit(this.optionChangedObj);
    } else {
      this.selectedChange.emit(event);
      this.optionChanged.emit(this.optionChangedObj);
      this.sortSelectedNUnselected(event);
      this.setValue(event.value, true);
      // for selectAll checkbox: set to true if selected length is same as dataSource
      this.selectAllSelected();
    }
  }

  sortSelectedNUnselected(event: MatSelectChange) {
    if (this.config?.multiple) {
      let sel: AnyType[] = [];
      let unSel: AnyType[] = [];
      this.dataSource?.forEach((item) => {
        if (event.value.indexOf && event.value.indexOf(item) !== -1) {
          sel.push(item);
        } else {
          unSel.push(item);
        }
      });
      if (this.config?.sortDataSource || this.config?.sortDataSource === undefined) {
        sel = this.sortDataSource(sel);
        unSel = this.sortDataSource(unSel);
        this.dataSource = [...sel, ...unSel];
      }
    }
  }

  /**
   * Set value for form control
   * @param value
   * @param emitEvent
   */
  private setValue(value: AnyType, emitEvent: boolean) {
    this.selected = value && JSON.parse(JSON.stringify(value));
    if (value && this.config?.inlineImage) {
      this.selectedInlineImage = Object.assign(
        {},
        ...(this.dataSource?.filter((x: AnyType) => {
          if (x._id && value._id) {
            return x._id === value._id;
          } else {
            return x.id === value.id;
          }
        }) ?? []),
      ).imageUrl;
    }
    this.firstPartOfLabel = this.getFistPartOfLabel();
    if (emitEvent && this.onChange) {
      this.onChange(value);
      this.onTouched();
    }
    // for multi-select
    if (value && value.length) {
      this.selectAllSelected();
    }
  }

  /**
   * Call from parent component to clean the labels
   */
  clearLabel() {
    this.firstPartOfLabel = '';
    this.selected = '';
  }

  /**
   * Mat-checkbox change event
   */
  onSelectAll(checked: boolean): void {
    this.allSelected = checked;
    const action = this.allSelected ? (item: AnyType) => item.select() : (item: AnyType) => item.deselect();
    this.matSelect?.options?.filter((item) => !item._text?.nativeElement.parentElement?.classList.contains('hide')).forEach(action);
  }

  /**
   * Input search event
   * @param event
   */
  onSearch(event: EventTarget): void {
    this.searchValue = (event as HTMLInputElement).value?.toLowerCase();
  }

  /**
   * Clear search on clicking cross
   */
  onClearSearch(): void {
    this.searchValue = '';
    if (this.config?.search) {
      setTimeout(() => {
        this.matSearch.nativeElement.value = '';
      }, 0);
    }
  }

  /**
   * Show only the searched option list and hide rest
   * @param option
   * @returns
   */
  hide(option: string): boolean {
    if (!this.config?.search || !this.searchValue || this.searchValue?.trim() === '') return false;
    return option === EMPTY_OPTION || !option.toLowerCase().includes(this.searchValue);
  }

  /**
   * child options' change events
   * @param eve
   */
  selectionChange(eve: MatOptionSelectionChange) {
    this.optionChangedObj = eve.source.value;
    this.optionChangedObj['fieldSelected'] = eve.source.selected;
  }

  /**
   * Get first part label
   * @returns
   */
  getFistPartOfLabel(): string {
    if (!this.selected) return '';
    let selected: AnyType;
    if (this.config?.multiple) {
      if (this.config.enableMatOptGroup || !this.config.enableSecondPartLabel) {
        selected = this.selected;
      } else {
        const secondPartOfLabel = this.getSecondPartOfLabel();
        selected = this.selected.slice(0, 2);
        if (secondPartOfLabel) selected.push(secondPartOfLabel);
      }
    } else {
      selected = this.selected;
      let selectedObj = this.dataSource?.find(
        (data) =>
          (data.id && data.id === selected.id) ||
          (data.id && data.id === selected._id) ||
          (data.id && data.id === selected) ||
          (data._id && data._id === selected._id) ||
          (data._id && data._id === selected.id) ||
          (data._id && data._id === selected) ||
          (data.key && data.key === selected.key) ||
          data === selected,
      );
      if (selectedObj?.name) {
        selectedObj.name = this.config?.titleCase ? this.titlecasePipe.transform(selectedObj.name) : selectedObj.name;
        selected = selectedObj;
      } else {
        selectedObj = this.config?.titleCase ? this.titlecasePipe.transform(selectedObj) : selectedObj;
        selected = selectedObj;
      }
    }
    return !selected ? EMPTY_OPTION : selected;
  }

  /**
   * Get second part label with selected count
   * @returns
   */
  getSecondPartOfLabel(): string {
    if (!this.config?.multiple || this.selected?.length <= 2) return '';
    const remainingQty = this.selected?.length - 2;
    return `(+${remainingQty})`; // (+2 other)
  }

  /**
   * Used with compare with for mat select to retrieve data on edit dialog
   * @param list
   * @param selected
   * @returns
   */
  compareMatSelect(list: AnyType, selected: AnyType) {
    if (list && selected) {
      if (list._id && selected._id && list._id === selected._id) {
        return true;
      } else if (list.name && list.name === selected) {
        return true;
      } else if (list === selected) {
        return true;
      } else if (list._id && list._id === selected) {
        return true;
      } else if (list.id && list.id === selected) {
        return true;
      } else if (list._id && selected.key && list._id === selected.key) {
        return true;
      } else if (list.key && selected.key && list.key === selected.key) {
        return true;
      } else if (list._id && selected.id && list._id === selected.id) {
        return true;
      } else if (list.id && selected.id && list.id === selected.id) {
        return true;
      } else if (list.id && selected._id && list.id === selected._id) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  searchFocus() {
    setTimeout(() => {
      this.matSearch?.nativeElement?.focus();
    }, 0);
  }

  /**
   * Select all selected conditions
   */
  selectAllSelected() {
    if (this.dataSource) {
      this.allSelected = this.selected && this.config?.multiple ? this.dataSource.length === this.selected.length : false;
      this.someSelected = !this.allSelected && this.selected && this.selected?.length > 0;
    }
  }

  remove(list: AnyType) {
    if (this.dataSource) {
      // todo: selected and unselected sorting not working right now. Will look for better solution.
      this.dataSource =
        !this.config?.sortDataSource && this.config?.sortDataSource !== undefined
          ? JSON.parse(JSON.stringify(this.dataSource))
          : JSON.parse(JSON.stringify(this.sortDataSource(this.dataSource)));
      this.removeChip.emit({ list, formControlName: this.formControlName });
    }
  }

  isArray(list: string | string[]): boolean {
    return Array.isArray(list);
  }

  /**
   * this function will add new option to the dropdown when user press enter
   * @param event
   */
  async addNewOptionInDropdown(event: AnyType) {
    if (this.config?.tagField) {
      const value: string = (event.srcElement.value || '').trim();
      const dataSourceValue = [{ _id: value, name: value }];
      const idx = this.dataSource?.findIndex((source) => source.name === dataSourceValue[0].name);
      if (idx === -1) {
        this.dataSource?.push(dataSourceValue[0]);
        // Add the new tag to the list
      }
      this.dataSource = this.sortDataSource(this.dataSource || []);
      this.optionChanged.emit(dataSourceValue[0]);
      // Clear the search input field
      this.onClearSearch();
    } else {
      // Check if the 'showAvatar' condition is met before proceeding
      if (!this.showAvatar) return;
      // Get the email input value, trim any white spaces
      const value: string = (event.srcElement.value || '').trim();
      // Check if the input value is a valid email address or a list of email addresses separated by white spaces, commas, or semi-colons
      if (this.isEmailValid(value)) {
        // Split the input value into an array of email addresses
        const splitValue: string[] = value.split(/[\s,;]+/);
        // Validate the email addresses and categorize them into matching and unmatched arrays
        const { matchingEmails, unmatchedEmails } = await this.validateEmails(splitValue);
        matchingEmails.forEach((user) => {
          const matchedObj = this.dataSource?.find((obj) => obj.email === user.email);
          if (!matchedObj) {
            this.dataSource?.push(user);
          }
        });
        if (this.selected.length) {
          this.selected.forEach((user: AnyType) => {
            const matchedObj = matchingEmails?.find((obj) => obj.email === user.email);
            if (!matchedObj) {
              matchingEmails.push(user);
            }
          });
        }
        this.dataSource = this.sortDataSource(this.dataSource || []);
        // Add the matching email addresses to the list
        this.setValue(matchingEmails, true);
        // Clear the search input field
        this.onClearSearch();
        // If there are any unmatched email addresses, show a confirmation dialog to inform the user
        if (unmatchedEmails.length) {
          this.confirmDialog.open({
            title: $localize`Unrecognized email address`,
            message:
              $localize`The email addresses` +
              ` \n\n <b>${unmatchedEmails.join('<br/>')}</b> \n\n ` +
              $localize`you entered do not match any user in our system.` +
              '<br/>' +
              $localize`Please ensure that you have entered the correct email addresses or` +
              '<br/>' +
              $localize`verify if the recipients are registered with our platform.`,
            cancelCaption: false,
          });
        }
      }
    }
  }

  /**
   * Check if the provided string is a valid email address or a list of email addresses separated by white spaces, commas, or semi-colons
   * @param value
   * @returns
   */
  private isEmailValid(value: string): boolean {
    // Check if the string contains any white spaces, commas, or semi-colons; if so, it is considered a list of email addresses
    return value.search(/[\s,;]+/) !== -1 || this.commonUtilityLib.validateEmail(value);
  }

  /**
   * This function validates an array of email addresses by checking if they exist in the
   * user database. It returns an object containing two arrays: matchingEmails and unmatchedEmails.
   * matchingEmails contains UserInfo objects for the email addresses that exist in the database,
   * while unmatchedEmails contains the email addresses that do not exist in the database.
   * @param emails
   * @returns
   */
  private validateEmails(emails: string[]): Promise<{ matchingEmails: UserInfo[]; unmatchedEmails: string[] }> {
    // Use the usersApiService to find all users by their email addresses
    return lastValueFrom(this.usersApiService.usersControllerFindAllByEmails(emails));
  }
}
